import VectorSource from "ol/source/Vector"
import VectorLayer from "ol/layer/Vector"
import { Feature, Geolocation } from "ol"
import { Point } from "ol/geom"
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style"
import { unByKey } from "ol/Observable"

const DOT_STYLE = new Style({
    image: new CircleStyle({ radius: 6, fill: new Fill({ color: "#0d6efd" }), stroke: new Stroke({ color: "#ffffff", width: 2 }) })
})
const ACCURACY_STYLE = new Style({
    fill: new Fill({ color: "rgba(13, 110, 253, .12)" }),
    stroke: new Stroke({ color: "rgba(13, 110, 253, .3)" })
})

// Locate-me button: watch the GPS position, center the view on the FIRST fix
// only, then track the dot without fighting user pans. Owns its layer, its
// Geolocation, and the button's state machine (idle | active | error).
export class Locate {
    #map
    #button
    #locateZoom
    #geolocation
    #source = new VectorSource()
    #layer
    #dotFeature = null
    #accuracyFeature = null
    #centered = false
    #keys = []
    #onClick
    #defaultTitle

    constructor(map, button, locateZoom) {
        this.#map = map
        this.#button = button
        this.#defaultTitle = button.title
        this.#locateZoom = locateZoom
        this.#layer = new VectorLayer({ source: this.#source, zIndex: 5 })
        map.addLayer(this.#layer)
        this.#geolocation = new Geolocation({
            projection: map.getView().getProjection(),
            trackingOptions: { enableHighAccuracy: true, timeout: 15_000 }
        })
        // OL updates position before accuracyGeometry — each handler touches only its own feature
        this.#keys.push(this.#geolocation.on("change:position", () => this.#positionChanged()))
        this.#keys.push(this.#geolocation.on("change:accuracyGeometry", () => this.#accuracyChanged()))
        // code 2 (POSITION_UNAVAILABLE) is a transient blip the still-running watch recovers
        // from on its own — e.g. Chromium's geolocation override briefly reports "unavailable"
        // while switching to a new fix. 1 (PERMISSION_DENIED) and 3 (TIMEOUT) surface as errors.
        // OL's watch isn't torn down synchronously with #stop() — a callback already in
        // flight must not mutate state from a just-stopped watch.
        this.#keys.push(this.#geolocation.on("error", (e) => { if (this.#button.dataset.state !== "active") return; if (e.code !== 2) this.#failed() }))
        this.#onClick = () => this.#toggle()
        button.addEventListener("click", this.#onClick)
        button.dataset.state = "idle"
    }

    get source() { return this.#source }

    dispose() {
        this.#geolocation.setTracking(false)
        this.#keys.forEach(unByKey)
        this.#keys = []
        this.#button.removeEventListener("click", this.#onClick)
        this.#button.title = this.#defaultTitle
        this.#button.dataset.state = "idle"
        this.#map.removeLayer(this.#layer)
    }

    #toggle() {
        if (this.#button.dataset.state === "active") this.#stop()
        else this.#start()
    }

    #start() {
        this.#button.dataset.state = "active"
        this.#button.title = this.#defaultTitle
        this.#centered = false
        this.#geolocation.setTracking(true)
    }

    #stop() {
        this.#geolocation.setTracking(false)
        this.#source.clear()
        this.#dotFeature = null
        this.#accuracyFeature = null
        this.#button.dataset.state = "idle"
    }

    #failed() {
        this.#stop()
        this.#button.dataset.state = "error"
        this.#button.title = "Standort nicht verfügbar — Berechtigung prüfen"
    }

    #positionChanged() {
        if (this.#button.dataset.state !== "active") return
        const coords = this.#geolocation.getPosition()
        if (!coords) return
        if (!this.#dotFeature) {
            this.#dotFeature = new Feature()
            this.#dotFeature.setStyle(DOT_STYLE)
            this.#source.addFeature(this.#dotFeature)
        }
        this.#dotFeature.setGeometry(new Point(coords))
        if (!this.#centered) {
            this.#centered = true
            const view = this.#map.getView()
            view.animate({ center: coords, zoom: Math.max(view.getZoom(), this.#locateZoom), duration: 300 })
        }
    }

    #accuracyChanged() {
        if (this.#button.dataset.state !== "active") return
        const geometry = this.#geolocation.getAccuracyGeometry()
        if (!geometry) return
        if (!this.#accuracyFeature) {
            this.#accuracyFeature = new Feature()
            this.#accuracyFeature.setStyle(ACCURACY_STYLE)
            this.#source.addFeature(this.#accuracyFeature)
        }
        this.#accuracyFeature.setGeometry(geometry)
    }
}
